import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Share2, X, Trophy, Star, FileCheck } from 'lucide-react';
import api from '../../lib/api';

/**
 * ShareTriggerBanner — Le déclencheur viral "Strava-like"
 * ─────────────────────────────────────────────────────────
 * Détecte les événements récents dignes d'être partagés :
 *   • Avis 5 étoiles reçu
 *   • Mandat signé
 *   • Palier d'avis atteint (10, 25, 50, 100)
 *
 * Affiche une bannière dorée invitant l'agent à publier sur ses
 * réseaux sociaux. Le clic mène vers /agent/studio-partage.
 *
 * Les bannières dismissées sont mémorisées 24h en localStorage
 * pour éviter le harcèlement (mais reviendront le lendemain).
 */
export default function ShareTriggerBanner() {
    const [trigger, setTrigger] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await api.get('/agent/me/share/triggers');
            const list = res.data?.triggers || [];
            if (list.length === 0) return;
            // Prendre le trigger le plus récent non-dismissé (dans les 24h)
            const first = list.find(t => !isDismissed(t));
            if (first) setTrigger(first);
        } catch {
            // Silent : pas de trigger n'est pas une erreur bloquante
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleDismiss = () => {
        if (trigger) markDismissed(trigger);
        setDismissed(true);
    };

    if (!trigger || dismissed) return null;

    const { icon: Icon, accent } = triggerVisuals(trigger.type);

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, y: -8, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.35 }}
                data-testid="share-trigger-banner"
                className="relative overflow-hidden rounded-2xl border border-[#A855F7]/30 bg-gradient-to-br from-[#1A0F2E] via-[#0B0913] to-[#050308] p-5 sm:p-6 mb-6"
            >
                {/* Halos DA Homly */}
                <div className="pointer-events-none absolute -top-20 -left-20 w-64 h-64 bg-[#681793]/25 blur-[80px] rounded-full" />
                <div className="pointer-events-none absolute -bottom-24 -right-16 w-72 h-72 bg-[#FF914D]/18 blur-[100px] rounded-full" />

                <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className={`shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br ${accent} flex items-center justify-center shadow-lg shadow-[#681793]/30`}>
                        <Icon className="w-7 h-7 text-white" />
                    </div>

                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[2.5px] text-[#FF914D] mb-1">
                            <Sparkles className="w-3.5 h-3.5" />
                            PRÊT À LE PARTAGER ?
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold text-white leading-snug">
                            {trigger.message}
                        </h3>
                        <p className="text-xs sm:text-sm text-[#C7BEE6] mt-1">
                            Transformez cette bonne nouvelle en visuel Instagram / LinkedIn en 1 clic.
                        </p>
                    </div>

                    <div className="flex items-center gap-2 sm:shrink-0">
                        <Link
                            to="/agent/studio-partage"
                            data-testid="share-trigger-cta"
                            className="group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-[#681793] to-[#FF914D] text-sm font-bold text-white shadow-lg shadow-[#681793]/30 hover:opacity-95 transition-all hover:scale-[1.02]"
                        >
                            <Share2 className="w-4 h-4" />
                            {trigger.cta || 'Le partager'}
                        </Link>
                        <button
                            onClick={handleDismiss}
                            aria-label="Fermer"
                            data-testid="share-trigger-dismiss"
                            className="p-2 rounded-full text-[#8A7DB8] hover:bg-white/5 hover:text-white transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
}

// ── Helpers ─────────────────────────────────────────────────────
const DISMISS_KEY = 'homly.shareTrigger.dismissed';
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24h

const triggerKey = (t) =>
    `${t.type}:${t.review_id || t.mandate_id || t.count || t.created_at || ''}`;

const isDismissed = (t) => {
    try {
        const raw = localStorage.getItem(DISMISS_KEY);
        if (!raw) return false;
        const obj = JSON.parse(raw);
        const ts = obj[triggerKey(t)];
        if (!ts) return false;
        return (Date.now() - ts) < DISMISS_TTL_MS;
    } catch {
        return false;
    }
};

const markDismissed = (t) => {
    try {
        const raw = localStorage.getItem(DISMISS_KEY);
        const obj = raw ? JSON.parse(raw) : {};
        obj[triggerKey(t)] = Date.now();
        // Nettoyer les entrées expirées
        Object.keys(obj).forEach(k => {
            if (Date.now() - obj[k] > DISMISS_TTL_MS) delete obj[k];
        });
        localStorage.setItem(DISMISS_KEY, JSON.stringify(obj));
    } catch {
        // localStorage indisponible : on ignore silencieusement
    }
};

const triggerVisuals = (type) => {
    if (type === 'review_5stars') return { icon: Star, accent: 'from-[#A855F7] to-[#FF914D]' };
    if (type === 'mandate_signed') return { icon: FileCheck, accent: 'from-[#681793] to-[#A855F7]' };
    if (type === 'milestone') return { icon: Trophy, accent: 'from-[#FF914D] to-[#A855F7]' };
    return { icon: Sparkles, accent: 'from-[#681793] to-[#FF914D]' };
};
